<template>
    <div
        :class="$style['summary']"
        itemtype="https://schema.org/Product"
        itemscope
    >
        <div :class="$style['summary__labels']">
            <ProductLabelList :labels="labels" />
        </div>

        <h2
            :class="[$style['summary__title'], 'sw-title']"
            itemprop="name"
        >
            {{ name }}
        </h2>

        <div
            itemtype="https://schema.org/Offer"
            itemprop="offers"
            itemscope
        >
            <meta
                v-if="$store.state.settings.default_currency"
                :content="$store.state.settings.default_currency"
                itemprop="priceCurrency"
            >

            <div :class="$style['summary__price_container']">
                <ProductPrice
                    :value="selectedOffer.price"
                    :class="$style['summary__price']"
                    itemprop="price"
                    bold
                    large
                />

                <div
                    v-if="bonusesCredit > 0"
                    :class="$style['summary__cashback']"
                >
                    <span>{{ $t('loyalty_cashback') }}</span>

                    <ProductPrice
                        v-tooltip="{
                            content: h => h('div', {
                                domProps: { innerHTML: $t('loyalty_bonus_tooltip') },
                            }),
                            centered: true,
                            placement: 'bottom-start',
                        }"
                        :value="bonusesCredit"
                        :class="$style['summary__bonus']"
                        itemprop="price"
                        bonus="credit"
                        bonus-sign
                        bold
                    />
                </div>
            </div>
        </div>

        <OfferPicker
            v-if="offersWithVariativeProperties.length > 1"
            :offers="offersWithVariativeProperties"
            :selected-offer-id="selectedOfferId"
            @select="selectOffer"
        />

        <div :class="$style['summary__buy']">
            <ProductAddControl
                :class="$style['summary__buy-button']"
                :offer-id="selectedOfferId"
                :store-quantity="selectedOffer.quantity"
                :product-type="type"
            />
        </div>

        <div
            v-if="article"
            :class="$style['summary__property']"
        >
            <div :class="[$style['summary__property-name'], 'sw-text']">
                {{ $t('product_article') }}
            </div>

            <div :class="[$style['summary__property-value'], 'sw-text']">
                {{ article }}
            </div>
        </div>

        <div
            v-if="selectedOffer.weight"
            :class="$style['summary__property']"
        >
            <div :class="[$style['summary__property-name'], 'sw-text']">
                {{ $t('product_weight') }}
            </div>

            <div :class="[$style['summary__property-value'], 'sw-text']">
                {{ selectedOffer.weight }}
            </div>
        </div>

        <div
            v-if="selectedOffer.boxLength"
            :class="$style['summary__property']"
        >
            <div :class="[$style['summary__property-name'], 'sw-text']">
                {{ $t('product_length') }}
            </div>

            <div :class="[$style['summary__property-value'], 'sw-text']">
                {{ selectedOffer.boxLength }}
            </div>
        </div>

        <div
            v-if="selectedOffer.boxWidth"
            :class="$style['summary__property']"
        >
            <div :class="[$style['summary__property-name'], 'sw-text']">
                {{ $t('product_width') }}
            </div>

            <div :class="[$style['summary__property-value'], 'sw-text']">
                {{ selectedOffer.boxWidth }}
            </div>
        </div>

        <div
            v-if="selectedOffer.boxHeight"
            :class="$style['summary__property']"
        >
            <div :class="[$style['summary__property-name'], 'sw-text']">
                {{ $t('product_height') }}
            </div>

            <div :class="[$style['summary__property-value'], 'sw-text']">
                {{ selectedOffer.boxHeight }}
            </div>
        </div>

        <template v-if="showCharacteristic">
            <div
                v-for="property in propertiesList"
                :key="property.id"
                :class="$style['summary__property']"
            >
                <div :class="[$style['summary__property-name'], 'sw-text']">
                    {{ property.name }}
                </div>

                <div :class="[$style['summary__property-value'], 'sw-text']">
                    {{ property.value }}
                </div>
            </div>

            <SwLink
                v-if="!expanded && properties.length > 2"
                :class="$style['summary__collapse-switch']"
                @click.native="expanded = !expanded"
            >
                {{ $t('expand_properties') }}
            </SwLink>
        </template>

        <div
            v-if="description"
            :class="[$style['summary__text'], 'sw-text']"
            itemprop="description"
        >
            <span :class="$style['summary__text_preserved']">{{ description }}</span>
        </div>

        <meta
            v-else
            :content="name"
            itemprop="description"
        >

        <meta
            v-if="selectedOffer && selectedOffer.images.length"
            :content="selectedOffer.images[0]"
            itemprop="image"
        >
    </div>
</template>

<script>
import OfferPicker from './ProductSummary/OfferPicker'
import ProductAddControl from '@@/website/components/common/ProductAddControl'
import ProductLabelList from '@@/website/components/common/ProductLabelList'
import ProductPrice from '@@/website/components/common/ProductPrice'

const findOfferInStock = offers => offers.find(offer => offer.quantity > 0) ?? null

export default {
  name: 'ProductSummary',

  components: {
    OfferPicker,
    ProductAddControl,
    ProductLabelList,
    ProductPrice
  },

  props: {
    name: {
      type: String,
      default: 'Название товара'
    },

    type: {
      type: String,
    },

    article: {
      type: String,
      default: ''
    },

    description: {
      type: String,
      default: ''
    },

    labels: {
      type: Array,
      default: () => []
    },

    offers: {
      type: Array,
      required: true,
      validator: offers => offers.length > 0
    },

    showCharacteristic: {
      type: Boolean,
      default: false
    },

    bonusesCredit: {
      type: Number,
      default: 0
    },
  },

  data: () => ({
    selectedOfferId: null,
    properties: [],
    expanded: false
  }),

  computed: {
    selectedOffer () {
      return this.offers.find(item => item.id === this.selectedOfferId) ?? this.mainOffer
    },

    propertiesList () {
      return !this.expanded
        ? this.properties.slice(0, 3)
        : this.properties
    },

    mainOffer () {
      return findOfferInStock(this.offers) ?? this.offers[0]
    },

    offersWithVariativeProperties () {
      const offers = []

      for (const offer of this.offers) {
        offers.push({
          ...offer,
          properties: offer.properties.filter(p => p.variative === true)
        })
      }

      return offers
    }
  },

  created () {
    this.selectedOfferId = this.mainOffer.id
    this.properties = this.mainOffer.properties

    this.$emit('updateImages', this.selectedOffer.images)
  },

  methods: {
    selectOffer (id) {
      this.selectedOfferId = id
      this.properties = this.selectedOffer.properties

      this.$emit('updateImages', this.selectedOffer.images)
    }
  }
}
</script>

<style lang="less" module>
@import (reference) "~@omnica/base/assets/stylesheets/variables.less";
@import (reference) "./assets/Web.less";

.summary {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  text-align: left;

  &__title {
    @media screen and (max-width: @bp-tablet) {
      font-size: 24px;
      line-height: 32px;
      margin-top: 0
    }
  }

  &__text {
    font-size: 16px;
    font-weight: 400;
    font-style: normal;
    line-height: 24px;
    margin-top: 24px;
    margin-bottom: 24px;

    &_preserved {
      white-space: pre-wrap;
    }
  }

  &__labels {
    display: flex;
    flex-direction: row;
    @media screen and (max-width: @bp-mobile-small) {
      display: none;
    }
  }

  &__price_container {
    display: flex;
    align-items: center;
    flex-direction: row;

    @media screen and (max-width: @bp-tablet) {
      margin: auto;
    }
  }

  &__price {
    display: block;
    line-height: 32px;
    margin-right: 14px;

    @media screen and (max-width: @bp-tablet) {
      font-size: 16px;
      line-height: 24px;
    }
  }

  &__cashback {
    display: block;
    font-size: 16px;
    color: @grey-800;
  }

  &__bonus {
    font-size: 16px;
    line-height: 24px;

    @media screen and (max-width: @bp-tablet) {
      font-size: 16px;
      line-height: 24px;
    }
  }

  &__buy {
    display: flex;
    flex-wrap: wrap;
    margin-top: 24px;
    margin-bottom: 24px;
  }

  &__buy-button {
    margin-bottom: 16px;
    @media screen and (min-width: @bp-mobile-small) {
      margin-right: 20px;
    }
    @media screen and (max-width: @bp-mobile-small) {
      width: 100%;
    }
  }

  &__collapse-switch {
    font-size: 16px;
    line-height: 24px;
    margin-top: 8px;
  }

  &__property {
    display: flex;
    flex-direction: row;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;

    & + & {
      margin-top: 8px;
    }
  }

  &__property-name {
    flex-basis: calc(55% - 24px);
    max-width: 160px;
    word-break: break-word;
    color: @grey-800;
    margin-right: 24px;
    @media screen and (max-width: @bp-mobile-small) {
      max-width: none;
    }
  }

  &__property-value {
    flex-basis: 45%;
    word-break: break-word;
  }
}
</style>
